/**
 * The height of the save bar on the 复习 settings page, as the page reserves
 * it.
 *
 * The bar is fixed to the foot of the window, so the page has to leave room
 * for it at the bottom and keep a focused control clear of it.
 * `modules/fuxi-settings` does both from `--cb-fuxi-settings-bar-height`,
 * falling back to one row when no script has run. The bar is one row on a wide
 * screen and two on a phone once "Unsaved changes" sits beside the button, so
 * this measures it and writes what it measured.
 *
 * A `ResizeObserver` measures it again whenever it changes height, which is
 * every time the unsaved line appears or a save swaps the form. A save replaces
 * the bar, and `./htmx-rebinding.ts` calls this again over what came back. A
 * bar the swap took away is measured as nothing, and that reading is ignored.
 */

import { boundOnce } from "./bound-once.js";

/** The bar, which says it is the save bar. */
const bar = "[data-cb-fuxi-settings-bar]";

/** The custom property the stylesheet reads. */
export const fuxiSettingsBarHeight = "--cb-fuxi-settings-bar-height";

/** The bars this bundle has not already measured. */
const notYetMeasured = boundOnce();

/** Write a bar's height onto the page, where the bar is still on it. */
function reserve(page: Document, measured: HTMLElement): void {
  if (!measured.isConnected) {
    return;
  }

  page.documentElement.style.setProperty(
    fuxiSettingsBarHeight,
    `${String(measured.offsetHeight)}px`,
  );
}

/**
 * Measure the save bar, where the page has one, and keep measuring it.
 *
 * @param page - The document whose root takes the height.
 * @param within - The document, or what an htmx swap just put into it.
 */
export function startFuxiSettingsBar(
  page: Document,
  within: ParentNode = page,
): void {
  for (const found of notYetMeasured(within, bar)) {
    reserve(page, found);

    if (typeof ResizeObserver !== "undefined") {
      new ResizeObserver(() => {
        reserve(page, found);
      }).observe(found);
    }
  }
}
